import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CallToAction from '../components/CallToAction';
import FadeInSection from '../components/FadeInSection';
import SeamlessBackground from '../components/SeamlessBackground';

const Contact: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        category: '',
        service: '',
        message: '',
        agreeToTerms: false
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Handle form submission
        console.log('Form submitted:', formData);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
        });
    };

    return (
        <div className="min-h-screen bg-[#022334] text-white font-sans antialiased relative">
            {/* Seamless CSS Background */}
            <SeamlessBackground />

            {/* Content */}
            <div className="relative z-10">
                <Navbar />
                <main className="relative">
                    {/* Hero/Contact Section */}
                    <FadeInSection delay={0.2}>
                        <section className="container mx-auto px-4 sm:px-6 md:px-12 lg:px-24 pt-8 sm:pt-16 lg:pt-24 pb-8 sm:pb-12 lg:pb-16">
                    <div className="max-w-7xl mx-auto">
                        {/* Heading */}
                        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                            <h1
                                className="mb-4 sm:mb-6 lg:mb-8"
                                style={{
                                    fontFamily: 'Manrope',
                                    fontWeight: 700,
                                    fontSize: 'clamp(32px, 8vw, 112px)',
                                    lineHeight: '1.3',
                                    letterSpacing: '-0.05em',
                                    color: '#FFFFFF'
                                }}
                            >
                                Let's talk hiring
                            </h1>
                            <p
                                style={{
                                    fontFamily: 'Manrope',
                                    fontWeight: 200,
                                    fontSize: 'clamp(14px, 2vw, 28px)',
                                    lineHeight: '1.4',
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    maxWidth: '900px',
                                    margin: '0 auto'
                                }}
                            >
                                Whether you're an employer looking for top talent or a candidate exploring new opportunities, we'd love to hear from you.
                            </p>
                        </div>

                        {/* Two Column Layout: Connect with us & Send Message Form */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
                            {/* Left Column - Connect with us */}
                            <div className="lg:col-span-4">
                                <h2
                                    className="mb-4 sm:mb-6 lg:mb-8"
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontWeight: 600,
                                        fontSize: 'clamp(24px, 4vw, 32px)',
                                        lineHeight: 'clamp(32px, 5vw, 44px)',
                                        color: '#FFFFFF'
                                    }}
                                >
                                    Connect with us
                                </h2>
                                <div className="flex gap-4">
                                    <a
                                        href="https://x.com/hushtalentinc"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 flex items-center justify-center rounded-lg transition-all hover:scale-110"
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}
                                        aria-label="X (Twitter)"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
                                        </svg>
                                    </a>
                                    <a
                                        href="https://www.instagram.com/hushtalentinc?igsh=MTF6cTVwOHNxMXdiMA%3D%3D&utm_source=qr"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 flex items-center justify-center rounded-lg transition-all hover:scale-110"
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}
                                        aria-label="Instagram"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" fill="currentColor"/>
                                        </svg>
                                    </a>
                                    <a
                                        href="#"
                                        className="w-12 h-12 flex items-center justify-center rounded-lg transition-all hover:scale-110"
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}
                                        aria-label="LinkedIn"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                                        </svg>
                                    </a>
                                </div>
                            </div>

                            {/* Right Column - Send Message Form */}
                            <div className="lg:col-span-8">
                                <div
                                    className="rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10"
                                    style={{
                                        background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
                                        boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08), 0px 6px 40px rgba(255, 255, 255, 0.08)',
                                        backdropFilter: 'blur(50px)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                    }}
                                >
                                    <h2
                                        className="mb-6 sm:mb-8"
                                        style={{
                                            fontFamily: 'Manrope',
                                            fontWeight: 600,
                                            fontSize: 'clamp(24px, 4vw, 32px)',
                                            lineHeight: 'clamp(32px, 5vw, 44px)',
                                            color: '#FFFFFF'
                                        }}
                                    >
                                        Send Message
                                    </h2>
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        {/* Row 1: Name, Phone, Business Email */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* Name Field */}
                                            <div>
                                                <div className="relative">
                                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        id="name"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full pl-11 pr-4 py-3.5 rounded-xl outline-none transition-all focus:ring-2 focus:ring-blue-400/50"
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.08)',
                                                            border: '1px solid rgba(255, 255, 255, 0.15)',
                                                            color: '#FFFFFF',
                                                            fontFamily: 'Inter',
                                                            fontSize: '14px'
                                                        }}
                                                        placeholder="Name"
                                                    />
                                                </div>
                                            </div>

                                            {/* Phone Field */}
                                            <div>
                                                <div className="relative">
                                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                                        <span className="text-xl">🇺🇸</span>
                                                    </div>
                                                    <input
                                                        type="tel"
                                                        id="phone"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full pl-14 pr-4 py-3.5 rounded-xl outline-none transition-all focus:ring-2 focus:ring-blue-400/50"
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.08)',
                                                            border: '1px solid rgba(255, 255, 255, 0.15)',
                                                            color: '#FFFFFF',
                                                            fontFamily: 'Inter',
                                                            fontSize: '14px'
                                                        }}
                                                        placeholder="Phone Number"
                                                    />
                                                </div>
                                            </div>

                                            {/* Business Email Field */}
                                            <div>
                                                <div className="relative">
                                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <path d="m22 6-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </div>
                                                    <input
                                                        type="email"
                                                        id="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full pl-11 pr-4 py-3.5 rounded-xl outline-none transition-all focus:ring-2 focus:ring-blue-400/50"
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.08)',
                                                            border: '1px solid rgba(255, 255, 255, 0.15)',
                                                            color: '#FFFFFF',
                                                            fontFamily: 'Inter',
                                                            fontSize: '14px'
                                                        }}
                                                        placeholder="Business Email"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Row 2: Category and Service */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Category Dropdown */}
                                            <div>
                                                <div className="relative">
                                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                                                            <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                                                            <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                                                            <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                                                        </svg>
                                                    </div>
                                                    <select
                                                        id="category"
                                                        name="category"
                                                        value={formData.category}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full pl-11 pr-10 py-3.5 rounded-xl outline-none transition-all focus:ring-2 focus:ring-blue-400/50 appearance-none cursor-pointer"
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.08)',
                                                            border: '1px solid rgba(255, 255, 255, 0.15)',
                                                            color: formData.category ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)',
                                                            fontFamily: 'Inter',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        <option value="" disabled>Category</option>
                                                        <option value="employer" style={{ background: '#022334', color: '#FFFFFF' }}>Employer</option>
                                                        <option value="candidate" style={{ background: '#022334', color: '#FFFFFF' }}>Candidate</option>
                                                        <option value="general" style={{ background: '#022334', color: '#FFFFFF' }}>General Inquiry</option>
                                                    </select>
                                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Service Dropdown */}
                                            <div>
                                                <div className="relative">
                                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </div>
                                                    <select
                                                        id="service"
                                                        name="service"
                                                        value={formData.service}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full pl-11 pr-10 py-3.5 rounded-xl outline-none transition-all focus:ring-2 focus:ring-blue-400/50 appearance-none cursor-pointer"
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.08)',
                                                            border: '1px solid rgba(255, 255, 255, 0.15)',
                                                            color: formData.service ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)',
                                                            fontFamily: 'Inter',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        <option value="" disabled>Service you are interested in</option>
                                                        <option value="recruiting" style={{ background: '#022334', color: '#FFFFFF' }}>Recruiting</option>
                                                        <option value="job-search" style={{ background: '#022334', color: '#FFFFFF' }}>Job Search</option>
                                                        <option value="consultation" style={{ background: '#022334', color: '#FFFFFF' }}>Consultation</option>
                                                        <option value="other" style={{ background: '#022334', color: '#FFFFFF' }}>Other</option>
                                                    </select>
                                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Row 3: Message */}
                                        <div>
                                            <div className="relative">
                                                <div className="absolute left-4 top-4 text-white/60">
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </div>
                                                <textarea
                                                    id="message"
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    required
                                                    rows={4}
                                                    className="w-full pl-11 pr-4 py-3.5 rounded-xl outline-none transition-all focus:ring-2 focus:ring-blue-400/50 resize-none"
                                                    style={{
                                                        background: 'rgba(255, 255, 255, 0.08)',
                                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                                        color: '#FFFFFF',
                                                        fontFamily: 'Inter',
                                                        fontSize: '14px'
                                                    }}
                                                    placeholder="Message"
                                                />
                                            </div>
                                        </div>

                                        {/* Terms Checkbox and Send Button Row */}
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 pt-2">
                                            {/* Terms Checkbox */}
                                            <div className="flex items-start gap-3 flex-1">
                                                <input
                                                    type="checkbox"
                                                    id="agreeToTerms"
                                                    name="agreeToTerms"
                                                    checked={formData.agreeToTerms}
                                                    onChange={handleChange}
                                                    required
                                                    className="mt-1 w-4 h-4 rounded border-2 border-white/30 bg-white/10 cursor-pointer flex-shrink-0"
                                                    style={{
                                                        accentColor: '#015FA6'
                                                    }}
                                                />
                                                <label
                                                    htmlFor="agreeToTerms"
                                                    className="cursor-pointer"
                                                    style={{
                                                        fontFamily: 'Inter',
                                                        fontSize: 'clamp(12px, 2vw, 13px)',
                                                        color: 'rgba(255, 255, 255, 0.7)',
                                                        lineHeight: '1.5'
                                                    }}
                                                >
                                                    I agree with Terms of Use and Privacy Policy
                                                </label>
                                            </div>

                                            {/* Submit Button */}
                                            <button
                                                type="submit"
                                                className="flex items-center justify-center gap-2 px-6 sm:px-8 py-3 rounded-xl transition-all hover:scale-105 w-full sm:w-auto sm:flex-shrink-0"
                                                style={{
                                                    background: 'linear-gradient(180deg, rgba(61, 66, 68, 0) -40.91%, rgba(158, 170, 176, 0.248) 132.95%)',
                                                    boxShadow: 'inset 0px 8px 10px rgba(246, 249, 255, 0.08)',
                                                    filter: 'drop-shadow(0px 6px 40px rgba(255, 255, 255, 0.08))',
                                                    backdropFilter: 'blur(50px)',
                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                    fontFamily: 'Inter',
                                                    fontWeight: 500,
                                                    fontSize: '15px',
                                                    color: '#FFFFFF'
                                                }}
                                            >
                                                Send
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                    </FadeInSection>

                    {/* CallToAction Section with subtle gradient */}
                    <FadeInSection delay={0.2}>
                        <div className="relative">
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background: 'radial-gradient(ellipse at center top, rgba(106, 212, 255, 0.1) 0%, transparent 70%)',
                                }}
                            />
                            <CallToAction />
                        </div>
                    </FadeInSection>
                </main>
                <Footer />
            </div>
        </div>
    );
};

export default Contact;
